import { Professional, ProfessionalLicense } from '@medprev-web-app/api-graphql-client';

import { SchedulesSearchProfessionalLicenses } from '@/app/(main)/(pages)/busca/[slug]/_types/search-partner-data.type';
import { Gender } from '@/types/auth-base.types';
import { FEMININE_HONORIFICS, MASCULINE_HONORIFICS } from '@/utils/formatters/professional-name';

export const CRM_COUNCIL_INITIALS = 'CRM';

export function hasAnyLicenseWithCRM(professional: { licenses?: ProfessionalLicense[] }): boolean {
  return professional.licenses?.some((license) => license.certificationBodyInitials === CRM_COUNCIL_INITIALS) || false;
}

export function isCrmLicense(license: SchedulesSearchProfessionalLicenses): boolean {
  return license.councilInitials === CRM_COUNCIL_INITIALS;
}

const GENERALIST_SPECIALTY = 'médico generalista';

export const isAnnounceableSpecialty = (
  specialtyName?: string | null,
  specialistQualificationRecord?: string | null,
): boolean =>
  Boolean(specialtyName?.trim())
  && specialtyName?.trim().toLowerCase() !== GENERALIST_SPECIALTY
  && Boolean(specialistQualificationRecord?.trim());

export const getAnnounceableSpecialties = (professional: Pick<Professional, 'licenses' | 'specialties'>) => {
  const specialties = professional.specialties ?? [];

  return hasAnyLicenseWithCRM(professional)
    ? specialties.filter(({ name, specialistQualificationRecord }) =>
        isAnnounceableSpecialty(name, specialistQualificationRecord))
    : specialties;
};

export const MAX_DISPLAYED_SPECIALTIES = 2;

export const formatAnnouncedSpecialties = (professional: Pick<Professional, 'licenses' | 'specialties'>): string[] => {
  const specialties = professional.specialties ?? [];

  if (!hasAnyLicenseWithCRM(professional)) {
    return specialties.map((specialty) => specialty.name).filter((name): name is string => Boolean(name));
  }

  return specialties
    .filter(({ name, specialistQualificationRecord, isDisplayed }) =>
      isDisplayed && isAnnounceableSpecialty(name, specialistQualificationRecord))
    .slice(0, MAX_DISPLAYED_SPECIALTIES)
    .map((specialty) => `${specialty.name} - RQE ${specialty.specialistQualificationRecord}`);
};

export interface ProfessionalCrmSuffixContext {
  gender?: string;
  honorific?: string;
}

export const getProfessionalCrmSuffix = ({ gender, honorific }: ProfessionalCrmSuffixContext): string => {
  const normalizedGender = gender?.toUpperCase();

  if (normalizedGender === Gender.MALE) {
    return 'MÉDICO';
  }

  if (normalizedGender === Gender.FEMALE) {
    return 'MÉDICA';
  }

  const normalizedHonorific = honorific?.trim().toLowerCase();

  if (MASCULINE_HONORIFICS.some((masculine) => masculine.toLowerCase() === normalizedHonorific)) {
    return 'MÉDICO';
  }

  if (FEMININE_HONORIFICS.some((feminine) => feminine.toLowerCase() === normalizedHonorific)) {
    return 'MÉDICA';
  }

  return 'MÉDICO (A)';
};

interface ProfessionalLicenseSource {
  certificationBodyInitials?: string | null;
  licenseRecord?: string | null;
  licenseState?: string | null;
}

interface ProfessionalSpecialtySource {
  name?: string | null;
  specialistQualificationRecord?: string | null;
  isDisplayed?: boolean | null;
}

export interface ProfessionalLicenseRowsSource {
  licenses?: ProfessionalLicenseSource[] | null;
  specialties?: ProfessionalSpecialtySource[] | null;
}

export function mapProfessionalLicenseRows(
  professional: ProfessionalLicenseRowsSource | null,
): SchedulesSearchProfessionalLicenses[] {
  const licenses = professional?.licenses ?? [];
  const license = licenses.find((current) => current.certificationBodyInitials === CRM_COUNCIL_INITIALS) ?? licenses[0];

  if (!license) {
    return [];
  }

  const councilInitials = license.certificationBodyInitials ?? '';
  const registration = {
    councilInitials,
    professionalLicenseRecord: license.licenseRecord ?? '',
    professionalLicenseState: license.licenseState ?? '',
  };

  const specialties = councilInitials === CRM_COUNCIL_INITIALS ? (professional?.specialties ?? []) : [];

  if (specialties.length === 0) {
    return [{ ...registration, specialistQualificationRecord: '', specialtyName: '', isDisplayed: false }];
  }

  return specialties.map((specialty) => ({
    ...registration,
    specialistQualificationRecord: specialty.specialistQualificationRecord ?? '',
    specialtyName: specialty.name ?? '',
    isDisplayed: specialty.isDisplayed ?? false,
  }));
}

export const formatLicenseInfo = (license: SchedulesSearchProfessionalLicenses, suffix?: string): string => {
  const { councilInitials, professionalLicenseRecord, professionalLicenseState } = license;
  const registration = `${councilInitials} ${professionalLicenseRecord} - ${professionalLicenseState}`;

  return suffix ? `${registration} • ${suffix}` : registration;
};

export const specialtyLineDisplay = (license: SchedulesSearchProfessionalLicenses): string =>
  `${license.specialtyName} - RQE ${license.specialistQualificationRecord}`;

export const formatProfessionalLicenses = (licenses: ProfessionalLicense[]): string => {
  return licenses
    .map((license) => `${license.certificationBodyInitials}/${license.licenseState} - ${license.licenseRecord}`)
    .join(', ');
};
